import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { unstable_batchedUpdates } from "react-dom"
import { FormProvider, useForm } from "react-hook-form"
import { store } from "react-notifications-component"
import { Route, useHistory, useRouteMatch } from "react-router"
import { notificationOptions } from "../../../api/Definitions"
import Loader from "../../../components/Loader/Loader"
import Seo from "../../../components/Seo/Seo"
import { iAccess } from "../../../definitions/Access"
import { iAsset } from "../../../definitions/Assets"
import {
  iInspectorProps,
  iResponse,
  iUrlParams,
} from "../../../definitions/interfaces"
import { ItemWithAccess } from "../../../definitions/types"
import { errorNotification } from "../../../helpers/notificationFunctions"
import {
  get,
  handleDeleteData,
  handleUpdateData,
} from "../../../store/data/actions"
import AssetHeader from "../Header"
import AssetOverview from "./Overview"

const AssetInspector: FunctionComponent<iInspectorProps<iAsset>> = ({
  item,
  access,
  reload,
}) => {
  const [isLoading, setLoading] = useState(false)
  const match = useRouteMatch<iUrlParams>()
  const history = useHistory()

  const methods = useForm<ItemWithAccess<iAsset>>({
    shouldUnregister: false,
    defaultValues: { ...item, __access: access },
  })

  const {
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    methods.reset({ ...item, __access: access })
  }, [item, access])

  const onSubmit = (data: ItemWithAccess<iAsset>) => {
    setLoading(true)
    handleUpdateData({
      data,
      fields: dirtyFields as Partial<ItemWithAccess<iAsset>>,
      link: "/v1/assets",
    })
      .then(() => {
        setLoading(false)
        reload()
        store.addNotification({
          title: "Success!",
          message: "Asset successfully updated!",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        if (e) {
          if (e.reason === "Nothing changed!") {
            store.addNotification({
              message: e.reason,
              type: "warning",
              ...notificationOptions,
            })
          } else {
            store.addNotification({
              message: e.reason,
              type: "danger",
              ...notificationOptions,
            })
          }
        } else {
          store.addNotification({
            message: "Something went wrong!",
            type: "danger",
            ...notificationOptions,
          })
        }
        setLoading(false)
      })
  }

  const onDelete = () => {
    setLoading(true)
    if (confirm("Are you sure you want to delete this?")) {
      handleDeleteData({
        link: "/v1/assets/" + item._id,
      })
        .then(() => {
          setLoading(false)
          store.addNotification({
            title: "Success!",
            message: "Asset successfully deleted!",
            // message: "teodosii@react-notifications-component",
            type: "success",
            ...notificationOptions,
          })
          history.goBack()
        })
        .catch((e) => {
          if (e) {
            if (e.reason === "Nothing changed!") {
              store.addNotification({
                message: e.reason,
                type: "warning",
                ...notificationOptions,
              })
            } else {
              store.addNotification({
                message: e.reason,
                type: "danger",
                ...notificationOptions,
              })
            }
          } else {
            store.addNotification({
              message: "Something went wrong!",
              type: "danger",
              ...notificationOptions,
            })
          }
          setLoading(false)
        })
    }
  }

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data as ItemWithAccess<iAsset>)
    })()
  }

  return (
    <FormProvider {...methods}>
      <Seo title={`${item.title ? item.title : "Inspector"} | VR-All-Art`} />
      <AssetHeader
        access={access}
        submitDeleteText={"Delete"}
        title={item.title || "Inspector"}
        submitText="Update"
        isLoading={isLoading}
        handleDelete={onDelete}
        handleSubmit={invokeSubmit}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <div className="inspector">
          <form
            onSubmit={methods.handleSubmit((data) => {
              onSubmit(data as ItemWithAccess<iAsset>)
            })}
            className="inspector-form"
          >
            <Route exact path={match.path}>
              <AssetOverview item={item} />
            </Route>
            <button className="d-none" type="submit">
              Submit
            </button>
          </form>
        </div>
      )}
    </FormProvider>
  )
}

const InspectorLoader = () => {
  const [isLoading, setLoading] = useState(true)
  const [access, setAccess] = useState<iAccess>()
  const [item, setItem] = useState<iAsset>()
  const match = useRouteMatch<iUrlParams>()

  const load = () => {
    Promise.all([
      get<iResponse<iAccess>>({
        link: "/v1/access",
        query: { collection: "Artifacts", url: match.params.url },
      }),
      get<iResponse<iAsset>>({ link: "/v2/assets/" + match.params.url }),
    ]).then((result) => {
      const [accessResult, dataResult] = result
      unstable_batchedUpdates(() => {
        setItem(dataResult.data)
        setAccess(accessResult.data)
        setLoading(false)
      })
    })
  }

  useMemo(() => {
    load()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item && access ? (
    <AssetInspector item={item} access={access} reload={load} />
  ) : null
}

export default InspectorLoader
